import React, { useState } from "react";
import { Box, Fab } from "@mui/material";
import ChatIcon from "@mui/icons-material/Chat";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import Chatbot from "./Chatbot"; // Ensure Chatbot is imported
import { ChatMessage } from "../../types/Chatbot/Chat";
import { useMutation } from "@tanstack/react-query";
import { postRetrievalData } from "../../services/chatbot";
import { transformChatDataToMessage } from "../../utils/parseTresleResponse";

// chat type will be the differentiation between the chatbot in the sidebar and the full chatbot
interface ChatbotContainerProps {
  chatType: "unit" | "insurance";
  metadata?: any;
}

// container for chatbot manages the chat state and sends messages
const ChatbotContainer: React.FC<ChatbotContainerProps> = ({ chatType, metadata }) => {
  const [showChat, setShowChat] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Error State
  const [errMessage, setErrMessage] = useState("Oops, something went wrong. Please check your connection and try again.");
  const [errorState, setErrorState] = useState(false);

  // Chat message state
  const [messages, setMessages] = useState<ChatMessage[]>([]);

  // Handle end chat
  const handleEndChat = () => {
    // Reset states and close the chat
    setShowChat(false);
    setErrMessage("");
    setMessages([]);
  };

  const mutation = useMutation({
    mutationFn: ({ prompt, chatType }: { prompt: string; chatType: "unit" | "insurance" }) => {
      let editedPrompt = prompt;
      console.log(metadata);

      // check for metadata and append to prompt
      if (chatType === "unit" && metadata?.unitId) {
        editedPrompt = `Given a user's questions, formulate SQL statement that is appropriate to answer the question from the AWS RDS instance.
        If a user asks a question that is not pertinent to your task let the user know you cannot answer their question. 
        Question: this question is for unit with unit id ${metadata.unitId}: ${prompt}`;
      }

      if (chatType === "insurance" && metadata?.unitId && metadata?.siteId && metadata?.projectId) {
        editedPrompt = `You are an admin assistant for an insurance claims company. 
        The following report refers to the amount of anomalous alerts for specifics metrics over the past 3 months.
        Your task is to summarize the contents of the report. Include only your summary in the final response. Report: ${metadata.reportData}`;
      }

      // The final mutation function receives the modified prompt
      return postRetrievalData({ prompt: editedPrompt, chatType, question: prompt });
    },
    onMutate: async ({ prompt, chatType }: { prompt: string; chatType: "unit" | "insurance" }) => {
      let tempIndex = -1;
      setMessages((prev) => {
        tempIndex = prev.length; // Save the current index of the new placeholder message
        return [...prev, { question: prompt, summary: "", explanation: "", sources: [] }];
      });
      setIsLoading(true);

      return tempIndex; // Pass the index of the placeholder message to the following mutation callbacks
    },
    onSuccess: (data, variables, context) => {
      console.log("onSuccess data from chatbot container", data);
      const message = transformChatDataToMessage(data);
      console.log("onSuccess message from chatbot container", message);
      // preserve original question and update the rest of the message
      setMessages((prev) => prev.map((msg, idx) => (idx === context ? { ...msg, summary: message.summary, explanation: message.explanation, sources: message.sources } : msg)));
    },
    onError: (error, variables, context) => {
      console.log(error);
      setErrorState(true);
      setErrMessage("Oops, something went wrong. Please check your connection and try again.");
      setMessages((prev) => prev.filter((_, idx) => idx !== context)); // Remove the placeholder on error
    },
    onSettled: () => {
      setIsLoading(false);
    },
  });

  // Trigger the mutation to send the message
  const handleSendMessage = (prompt: string) => {
    if (prompt.trim()) {
      console.log(prompt, "prompt-----------------");
      mutation.mutate({ prompt, chatType });
    }
  };

  console.log("messages", messages);

  return (
    <>
      {/* Chatbot Popup */}
      {(showChat || chatType === "insurance") && (
        <Box>
          <Chatbot
            onClose={() => setShowChat(false)}
            isError={errorState}
            errMessage={errMessage}
            handleEndChat={handleEndChat}
            handleSendMessage={handleSendMessage}
            messages={messages}
            isLoading={isLoading}
            setIsError={setErrorState}
            withHeader={chatType === "unit"}
          />
        </Box>
      )}

      {/* Only show fab for mini chat bot */}
      {chatType === "unit" && (
        <Fab
          aria-label="chat"
          sx={{
            position: "fixed",
            bottom: 16,
            right: 16,
            zIndex: 1000,
            backgroundColor: showChat ? "grey.300" : "primary.light",
            "&:hover": {
              backgroundColor: showChat ? "grey.500" : "primary.dark",
            },
            color: showChat ? "black" : "white",
          }}
          onClick={() => setShowChat((prev) => !prev)} // Toggle chat
        >
          {showChat ? <KeyboardArrowDownIcon /> : <ChatIcon />}
        </Fab>
      )}
    </>
  );
};

export default ChatbotContainer;
